import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/lib/api";
import { Task, TaskCreateRequest, TaskUpdateRequest, TaskComment } from "@/lib/types";
import { getErrorMessage } from "@/lib/utils";
import { toast } from "sonner";

export function useTasks() {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ["tasks"],
    queryFn: () => apiClient.getTasks(),
  });
  const createTask = useMutation({
    mutationFn: (data: TaskCreateRequest) => apiClient.createTask(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
      queryClient.invalidateQueries({ queryKey: ["tasks", "lead-dashboard"] });
      toast.success("Task created");
    },
    onError: (e: unknown) => toast.error(getErrorMessage(e, "Failed to create task")),
  });
  const updateTask = useMutation({
    mutationFn: ({ id, data }: { id: string; data: TaskUpdateRequest }) => apiClient.updateTask(id, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
      queryClient.invalidateQueries({ queryKey: ["task", variables.id] });
      queryClient.invalidateQueries({ queryKey: ["tasks", "lead-dashboard"] });
      toast.success("Task updated");
    },
    onError: (e: unknown) => toast.error(getErrorMessage(e, "Failed to update task")),
  });
  const updateTaskStatus = useMutation({
    mutationFn: ({ id, stageId }: { id: string; stageId: string }) => apiClient.updateTaskStatus(id, stageId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
      queryClient.invalidateQueries({ queryKey: ["task", variables.id] });
      queryClient.invalidateQueries({ queryKey: ["tasks", "lead-dashboard"] });
      toast.success("Task updated");
    },
    onError: (e: unknown) => toast.error(getErrorMessage(e, "Failed to update task")),
  });
  const deleteTask = useMutation({
    mutationFn: (id: string) => apiClient.deleteTask(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
      queryClient.invalidateQueries({ queryKey: ["tasks", "lead-dashboard"] });
      toast.success("Task deleted");
    },
    onError: (e: unknown) => toast.error(getErrorMessage(e, "Failed to delete task")),
  });
  return {
    tasks: (query.data ?? []) as Task[],
    isLoading: query.isLoading,
    error: query.error,
    createTask,
    updateTask,
    updateTaskStatus,
    deleteTask,
    refetch: () => queryClient.invalidateQueries({ queryKey: ["tasks"] }),
  };
}

export function useTaskById(id: string | undefined) {
  return useQuery({
    queryKey: ["task", id],
    queryFn: () => apiClient.getTaskById(id!),
    enabled: !!id,
  });
}

export function useTasksByAgent(agentId: string | undefined) {
  return useQuery({
    queryKey: ["tasks", "agent", agentId],
    queryFn: () => apiClient.getTasksByAgent(agentId!),
    enabled: !!agentId,
  });
}

/** Team Leader — GET /api/tasks/lead-dashboard */
export function useLeadDashboard(enabled: boolean) {
  return useQuery({
    queryKey: ["tasks", "lead-dashboard"],
    queryFn: () => apiClient.getLeadDashboard(),
    enabled,
  });
}

/** Task comments (internal chat) for a task */
export function useTaskComments(taskId: string | undefined) {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ["task", taskId, "comments"],
    queryFn: () => apiClient.getTaskComments(taskId!),
    enabled: !!taskId,
  });

  const addComment = useMutation({
    mutationFn: (data: { content: string; attachmentsIds?: string[] }) =>
      apiClient.addTaskComment(taskId!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["task", taskId, "comments"] });
      queryClient.invalidateQueries({ queryKey: ["task", taskId] });
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
      queryClient.invalidateQueries({ queryKey: ["tasks", "lead-dashboard"] });
      toast.success("Comment added");
    },
    onError: (e: unknown) => toast.error(getErrorMessage(e, "Failed to add comment")),
  });

  const updateComment = useMutation({
    mutationFn: ({
      commentId,
      content,
      attachmentsIds,
    }: {
      commentId: string;
      content: string;
      attachmentsIds?: string[];
    }) => apiClient.updateTaskComment(commentId, { content, attachmentsIds }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["task", taskId, "comments"] });
      queryClient.invalidateQueries({ queryKey: ["task", taskId] });
      toast.success("Comment updated");
    },
    onError: (e: unknown) => toast.error(getErrorMessage(e, "Failed to update comment")),
  });

  const deleteComment = useMutation({
    mutationFn: (commentId: string) => apiClient.deleteTaskComment(commentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["task", taskId, "comments"] });
      queryClient.invalidateQueries({ queryKey: ["task", taskId] });
      toast.success("Comment deleted");
    },
    onError: (e: unknown) => toast.error(getErrorMessage(e, "Failed to delete comment")),
  });

  return {
    comments: (query.data ?? []) as TaskComment[],
    isLoading: query.isLoading,
    error: query.error,
    refetch: () => queryClient.invalidateQueries({ queryKey: ["task", taskId, "comments"] }),
    addComment,
    updateComment,
    deleteComment,
  };
}
